import { useState, useMemo } from "react";
import { SEO } from "@/components/seo/SEO";
import { useBookmarks } from "@/hooks/useBookmarks";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useExamContext } from "@/contexts/ExamContext";
import { TopicSelect } from "@/components/filters/TopicSelect";
import { DifficultySelect } from "@/components/filters/DifficultySelect";
import { ModernMCQCard } from "@/components/practice/ModernMCQCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bookmark, BookmarkX, ChevronLeft, ChevronRight, Filter, RotateCcw } from "lucide-react";
import { UniversalBreadcrumb } from "@/components/navigation/UniversalBreadcrumb";

const sb = supabase as any;

export default function Bookmarks() {
  const { user } = useAuth();
  const { bookmarks, isLoading: bookmarksLoading, toggleBookmark } = useBookmarks();
  const { selectedExamSlug } = useExamContext();
  const [topicFilter, setTopicFilter] = useState<string | undefined>();
  const [difficultyFilter, setDifficultyFilter] = useState<string | undefined>();
  const [index, setIndex] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);

  const mcqIds = useMemo(() => bookmarks.map((b) => b.mcq_id), [bookmarks]);

  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await sb.from("topics").select("id,name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: mcqs = [], isLoading: mcqsLoading } = useQuery({
    queryKey: ["bookmarked-mcqs", mcqIds],
    enabled: mcqIds.length > 0,
    queryFn: async () => {
      const { data, error } = await sb
        .from("mcqs")
        .select("id, topic_id, stem, option_a, option_b, option_c, option_d, option_e, correct_option, explanation, difficulty, tags")
        .eq("lifecycle_status", "active")
        .in("id", mcqIds);
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id,
        topic_id: row.topic_id,
        stem: row.stem,
        options: [row.option_a, row.option_b, row.option_c, row.option_d, row.option_e].filter(Boolean),
        correctIndex: ["A", "B", "C", "D", "E"].indexOf(row.correct_option),
        explanation: row.explanation,
        difficulty: row.difficulty,
        tags: row.tags,
      }));
    },
  });

  const filtered = useMemo(() => {
    let result = mcqs;
    if (topicFilter) result = result.filter((m: any) => m.topic_id === topicFilter);
    if (difficultyFilter) result = result.filter((m: any) => String(m.difficulty) === difficultyFilter);
    return result;
  }, [mcqs, topicFilter, difficultyFilter]);

  const currentMcq = filtered[index];
  const isLoading = bookmarksLoading || mcqsLoading;

  const handleNext = () => {
    if (index < filtered.length - 1) setIndex(index + 1);
  };
  const handlePrev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const resetFilters = () => {
    setTopicFilter(undefined);
    setDifficultyFilter(undefined);
    setIndex(0);
  };

  return (
    <>
      <SEO title="Bookmarked Questions | Medic Mastery" description="Review your bookmarked and flagged questions" />
      <div className="space-y-6 max-w-4xl mx-auto">
        <UniversalBreadcrumb />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bookmark className="h-6 w-6 text-primary" />
              Bookmarked Questions
            </h1>
            <p className="text-muted-foreground mt-1">
              {filtered.length} bookmarked question{filtered.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            variant={reviewMode ? "default" : "outline"}
            onClick={() => { setReviewMode(!reviewMode); setIndex(0); }}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {reviewMode ? "Exit Review" : "Review Flagged"}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <TopicSelect topics={topics} value={topicFilter} onChange={(v) => { setTopicFilter(v); setIndex(0); }} />
          <DifficultySelect value={difficultyFilter} onChange={(v) => { setDifficultyFilter(v); setIndex(0); }} />
          {(topicFilter || difficultyFilter) && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <Filter className="h-3 w-3 mr-1" /> Clear filters
            </Button>
          )}
        </div>

        {isLoading ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">Loading bookmarks...</CardContent></Card>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <Bookmark className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="text-lg font-medium">No bookmarked questions</p>
              <p className="text-sm text-muted-foreground">
                {mcqs.length > 0
                  ? "No questions match your current filters."
                  : "Bookmark questions during practice to review them here."}
              </p>
            </CardContent>
          </Card>
        ) : reviewMode && currentMcq ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="secondary">
                {index + 1} / {filtered.length}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleBookmark(currentMcq.id)}
                className="text-destructive hover:text-destructive"
              >
                <BookmarkX className="h-4 w-4 mr-1" /> Remove bookmark
              </Button>
            </div>

            <ModernMCQCard
              mcq={currentMcq}
              mode="review"
              onSubmit={() => {}}
            />

            <div className="flex justify-between">
              <Button variant="outline" onClick={handlePrev} disabled={index === 0}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <Button variant="outline" onClick={handleNext} disabled={index >= filtered.length - 1}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((mcq: any, i: number) => (
              <Card key={mcq.id} className="hover:border-primary/30 transition-colors cursor-pointer" onClick={() => { setReviewMode(true); setIndex(i); }}>
                <CardContent className="py-4 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium line-clamp-2">{mcq.stem}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="secondary">Diff {mcq.difficulty ?? 3}</Badge>
                      {mcq.tags?.slice(0, 2).map((t: string) => (
                        <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={(e) => { e.stopPropagation(); toggleBookmark(mcq.id); }}
                  >
                    <BookmarkX className="h-4 w-4 text-destructive" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
